import { useCallback, useState } from "react";
import { isCompanyAssignedToUser } from "./companyListUtils";
import {
  formatManagerName,
  isSubscriptionAssignedToUser,
} from "./subscriptionAlertUtils";

export const REPORTS_SHOW_MINE_ONLY_KEY = "reports_show_mine_only";

export const REPORTS_MANAGER_SEARCH_PLACEHOLDER =
  "Поиск по ИНН, названию и ФИО ответственного и менеджера сопровождения…";

export function readReportsShowMineOnly() {
  return localStorage.getItem(REPORTS_SHOW_MINE_ONLY_KEY) === "true";
}

export function saveReportsShowMineOnly(value) {
  try {
    localStorage.setItem(REPORTS_SHOW_MINE_ONLY_KEY, value ? "true" : "false");
  } catch {
    /* ignore */
  }
}

export function useReportsShowMineOnly() {
  const [showMineOnly, setShowMineOnly] = useState(readReportsShowMineOnly);
  const setShow = useCallback((next) => {
    setShowMineOnly(next);
    saveReportsShowMineOnly(next);
  }, []);
  return [showMineOnly, setShow];
}

/** API max_period принимает целое число дней */
export function normalizeSubscriptionMaxPeriod(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return 30;
  }
  return Math.max(0, Math.ceil(n));
}

export function subscriptionDaysFromCalendar(endDateIso, minDayIso) {
  const end = new Date(endDateIso);
  const start = minDayIso ? new Date(minDayIso) : new Date();
  const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return normalizeSubscriptionMaxPeriod(days);
}

/** @param {Array|null|undefined} companys @param {string|number|null|undefined} inn */
export function findCompanyByInn(companys, inn) {
  if (inn == null || !Array.isArray(companys)) {
    return null;
  }
  return companys.find((c) => String(c.inn) === String(inn)) ?? null;
}

/** @param {object|null|undefined} company */
export function resolveManagersForCompany(company) {
  return {
    responsible: formatManagerName(company?.manager ?? null),
    support: formatManagerName(company?.support_manager ?? null),
  };
}

/** @param {object} item @param {Array|null|undefined} companys */
export function resolveManagersForSubscriptionItem(item, companys) {
  const rel = item?.company_rel;
  if (rel?.manager || rel?.support_manager) {
    return {
      responsible: formatManagerName(rel.manager ?? null),
      support: formatManagerName(rel.support_manager ?? null),
    };
  }
  const company = findCompanyByInn(companys, rel?.inn);
  return resolveManagersForCompany(company);
}

/**
 * @param {{ inn?: string, nameCompany?: string, managers?: { responsible: { text: string, muted: boolean }, support: { text: string, muted: boolean } } }} ctx
 * @param {string} query
 */
export function matchesReportManagerSearch(ctx, query) {
  if (!query || query.length <= 1) {
    return true;
  }
  const q = query.toLowerCase();
  const inn = String(ctx.inn ?? "").toLowerCase();
  const name = String(ctx.nameCompany ?? "").toLowerCase();
  const resp = ctx.managers?.responsible;
  const sup = ctx.managers?.support;
  const respFio =
    resp && !resp.muted ? resp.text.toLowerCase() : "";
  const supFio = sup && !sup.muted ? sup.text.toLowerCase() : "";
  return (
    inn.includes(q) ||
    name.includes(q) ||
    (respFio && respFio.includes(q)) ||
    (supFio && supFio.includes(q))
  );
}

/**
 * @param {Array} rows — элементы с полем inn
 * @param {{ searchQuery?: string, showMineOnly?: boolean, currentUserId?: string|number, companys?: Array|null, getNameCompany?: (row) => string }} options
 */
export function filterRowsByInn(rows, options = {}) {
  if (!Array.isArray(rows)) {
    return [];
  }
  const {
    searchQuery = "",
    showMineOnly = false,
    currentUserId,
    companys,
    getNameCompany = (row) => row.name_company ?? "",
  } = options;

  return rows.filter((row) => {
    const inn = row.inn;
    const company = findCompanyByInn(companys, inn);
    if (showMineOnly && !isCompanyAssignedToUser(company, currentUserId)) {
      return false;
    }
    const managers = resolveManagersForCompany(company);
    return matchesReportManagerSearch(
      {
        inn,
        nameCompany: getNameCompany(row),
        managers,
      },
      searchQuery,
    );
  });
}

/**
 * @param {Array} rows
 * @param {{ searchQuery?: string, showMineOnly?: boolean, currentUserId?: string|number, companys?: Array|null }} options
 */
export function filterSubscriptionReportRows(rows, options = {}) {
  if (!Array.isArray(rows)) {
    return [];
  }
  const {
    searchQuery = "",
    showMineOnly = false,
    currentUserId,
    companys,
  } = options;

  return rows.filter((item) => {
    if (
      showMineOnly &&
      !isSubscriptionAssignedToUser(item, companys, currentUserId)
    ) {
      return false;
    }
    const rel = item.company_rel;
    const managers = resolveManagersForSubscriptionItem(item, companys);
    return matchesReportManagerSearch(
      {
        inn: rel?.inn,
        nameCompany: rel?.name_company,
        managers,
      },
      searchQuery,
    );
  });
}

/** @param {string} inn @param {Array|null|undefined} companys */
export function managerExcelCellsForInn(inn, companys) {
  const { responsible, support } = resolveManagersForCompany(
    findCompanyByInn(companys, inn),
  );
  return {
    "Ответственный менеджер": responsible.text,
    "Менеджер сопровождения": support.text,
  };
}

/** @param {object} item @param {Array|null|undefined} companys */
export function managerExcelCellsForSubscriptionItem(item, companys) {
  const { responsible, support } = resolveManagersForSubscriptionItem(
    item,
    companys,
  );
  return {
    "Ответственный менеджер": responsible.text,
    "Менеджер сопровождения": support.text,
  };
}
